import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown, Phone, Mail, MapPin, ArrowUpRight } from 'lucide-react';

// Sprog- og rutemapping for at kunne skifte sprog på den aktuelle side
const routeMap = {
  da: {
    index: '/',
    marine: '/marine',
    industri: '/industri',
    kraftvaerker: '/kraftvaerker',
    fjernvarme: '/fjernvarme',
    turbine: '/turbine-isolering',
    cases: '/cases',
    nyheder: '/nyheder',
    om: '/om',
    kontakt: '/kontakt',
    privatlivspolitik: '/privatlivspolitik'
  },
  en: {
    index: '/en',
    marine: '/en/marine',
    industri: '/en/industry',
    kraftvaerker: '/en/power-plants',
    fjernvarme: '/en/district-heating',
    turbine: '/en/turbine-insulation',
    cases: '/en/cases',
    nyheder: '/en/news',
    om: '/en/about',
    kontakt: '/en/contact',
    privatlivspolitik: '/en/privacy-policy'
  }
};

// Find den tilsvarende rute på det andet sprog
function getAlternativeRoute(pathname) {
  const isEn = pathname.startsWith('/en');
  const currentLang = isEn ? 'en' : 'da';
  const targetLang = isEn ? 'da' : 'en';
  
  // Find rutenøglen (f.eks. 'om')
  const routeKey = Object.keys(routeMap[currentLang]).find(
    key => routeMap[currentLang][key] === pathname
  );
  
  if (routeKey) {
    return routeMap[targetLang][routeKey];
  }
  
  // Håndtering af detaljesider (cases og nyheder)
  if (pathname.includes('/cases/')) {
    return isEn ? '/cases' : '/en/cases';
  }
  if (pathname.includes('/nyheder/') || pathname.includes('/news/')) {
    return isEn ? '/nyheder' : '/en/news';
  }
  
  return isEn ? '/' : '/en';
}

export function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);
  return null;
}

export default function Layout({ children }) {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const isEn = pathname.startsWith('/en');
  const lang = isEn ? 'en' : 'da';
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);

  // Luk dropdowns ved sideskift
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileSubmenuOpen(false);
    setServicesDropdownOpen(false);
  }, [pathname]);

  const menuItems = isEn ? [
    { label: 'Home', href: '/en' },
    {
      label: 'Products & Services',
      href: '#',
      children: [
        { label: 'Marine', href: '/en/marine' },
        { label: 'District Heating', href: '/en/district-heating' },
        { label: 'CHP Power Plants', href: '/en/power-plants' },
        { label: 'Turbine Insulation', href: '/en/turbine-insulation' },
        { label: 'Industrial Insulation', href: '/en/industry' }
      ]
    },
    { label: 'Case Studies', href: '/en/cases' },
    { label: 'News', href: '/en/news' },
    { label: 'About Us', href: '/en/about' },
    { label: 'Contact', href: '/en/contact' }
  ] : [
    { label: 'Forside', href: '/' },
    {
      label: 'Produkter & Services',
      href: '#',
      children: [
        { label: 'Marine', href: '/marine' },
        { label: 'Fjernvarme', href: '/fjernvarme' },
        { label: 'Kraftvarmeværker', href: '/kraftvaerker' },
        { label: 'Turbine-isolering', href: '/turbine-isolering' },
        { label: 'Industriisolering', href: '/industri' }
      ]
    },
    { label: 'Cases', href: '/cases' },
    { label: 'Nyheder', href: '/nyheder' },
    { label: 'Om os', href: '/om' },
    { label: 'Kontakt', href: '/kontakt' }
  ];

  const handleLanguageSwitch = () => {
    const targetRoute = getAlternativeRoute(pathname);
    navigate(targetRoute);
  };

  const isActive = (href, children) => {
    if (children) {
      return children.some(child => {
        const basePath = child.href.split('?')[0];
        return pathname === basePath;
      });
    }
    if (href === '/' || href === '/en') {
      return pathname === href;
    }
    return pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen flex flex-col bg-brand-dark text-gray-300 font-sans selection:bg-brand-accent selection:text-white">
      <ScrollToTop />
      
      {/* Main Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        {/* Top Row: Logo and Actions */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex justify-between items-center">
          {/* Logo */}
          <Link to={isEn ? '/en' : '/'} className="flex flex-col group">
            <span className="font-display font-black text-gray-900 text-2xl uppercase tracking-tight leading-none">
              FREDERICIA
            </span>
            <span className="text-xs text-brand-accent font-mono font-black uppercase tracking-widest leading-none mt-1">
              ISOLERINGSPUDER
            </span>
          </Link>

          {/* Right Actions (Desktop) */}
          <div className="hidden min-[1200px]:flex items-center gap-4">
            {/* Language Selector */}
            <button
              onClick={handleLanguageSwitch}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-none border border-gray-200 hover:border-gray-300 hover:text-gray-900 transition-colors text-xs font-medium font-mono text-gray-600 cursor-pointer"
            >
              <Globe className="h-3.5 w-3.5 text-brand-accent" />
              {isEn ? 'DA' : 'EN'}
            </button>

            {/* CTA Button */}
            <Link
              to={isEn ? '/en/contact' : '/kontakt'}
              className="bg-brand-accent hover:bg-brand-accent-hover text-white text-sm font-bold uppercase tracking-wider px-5 py-2.5 rounded-none transition-colors shadow-lg shadow-brand-accent/10"
            >
              {isEn ? 'Get a Quote' : 'Få et tilbud'}
            </Link>
          </div>

          {/* Mobile Menu Button (Under 1200px) */}
          <div className="flex min-[1200px]:hidden items-center gap-3">
            {/* Language Selector Mobile */}
            <button
              onClick={handleLanguageSwitch}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-none border border-gray-200 text-xs font-mono text-gray-600 cursor-pointer"
            >
              <Globe className="h-3.5 w-3.5 text-brand-accent" />
              {isEn ? 'DA' : 'EN'}
            </button>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-1 text-gray-600 hover:text-brand-accent transition-colors cursor-pointer"
            >
              {mobileMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
            </button>
          </div>
        </div>

        {/* Bottom Row: Left-aligned Menu Links with Slanted Orange Section (Desktop only) */}
        <div className="hidden min-[1200px]:block border-t border-gray-100 bg-gray-50 relative h-12">
          {/* Slanted Orange Section at the very left edge of the screen, extending past the menu links */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute left-0 top-0 h-full w-[1350px] bg-brand-accent transform -skew-x-12 -translate-x-12 z-10" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center relative">
            <nav className="flex items-center gap-8 h-full relative z-20">
              {menuItems.map((item, idx) => {
                if (item.children) {
                  const active = isActive(item.href, item.children);
                  return (
                    <div key={idx} className="relative group h-full flex items-center">
                      <button
                        className={`text-xs font-bold hover:text-white transition-colors relative py-2 uppercase h-full flex items-center gap-1.5 cursor-pointer ${
                          active ? 'text-white font-black border-b-2 border-white' : 'text-white/90'
                        }`}
                      >
                        {item.label}
                        <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
                      </button>
                      
                      {/* Dropdown Menu */}
                      <div className="absolute top-full left-0 mt-0 w-64 bg-white border border-gray-200 rounded-none shadow-xl py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                        {item.children.map((child, cIdx) => (
                          <Link
                            key={cIdx}
                            to={child.href}
                            className={`block px-4 py-2.5 text-xs hover:bg-gray-50 hover:text-brand-accent transition-colors uppercase ${
                              pathname + search === child.href ? 'text-brand-accent bg-gray-50 font-black' : 'text-gray-600'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }

                const active = isActive(item.href);
                return (
                  <Link
                    key={idx}
                    to={item.href}
                    className={`text-xs font-bold hover:text-white transition-colors relative py-2 uppercase h-full flex items-center ${
                      active ? 'text-white font-black border-b-2 border-white' : 'text-white/90'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="min-[1200px]:hidden border-t border-gray-200 bg-white px-4 py-6 space-y-4 max-h-[calc(100vh-80px)] overflow-y-auto">
            {menuItems.map((item, idx) => {
              if (item.children) {
                return (
                  <div key={idx} className="space-y-1">
                    <button
                      onClick={() => setMobileSubmenuOpen(!mobileSubmenuOpen)}
                      className={`w-full text-left px-3 py-2 rounded text-base font-medium hover:text-gray-900 hover:bg-gray-50 transition-colors uppercase flex justify-between items-center cursor-pointer ${
                        isActive(item.href, item.children) ? 'text-black bg-gray-50 font-bold' : 'text-gray-600'
                      }`}
                    >
                      <span>{item.label}</span>
                      <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${mobileSubmenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {mobileSubmenuOpen && (
                      <div className="pl-6 space-y-1">
                        {item.children.map((child, cIdx) => (
                          <Link
                            key={cIdx}
                            to={child.href}
                            className={`block py-2 text-sm hover:text-gray-900 transition-colors uppercase ${
                              pathname + search === child.href ? 'text-black font-bold' : 'text-gray-500'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={idx}
                  to={item.href}
                  className={`block px-3 py-2 rounded text-base font-medium hover:text-gray-900 hover:bg-gray-50 transition-colors uppercase ${
                    isActive(item.href) ? 'text-black bg-gray-50 font-bold' : 'text-gray-600'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}

            <div className="pt-4 border-t border-gray-200">
              <Link
                to={isEn ? '/en/contact' : '/kontakt'}
                className="block w-full text-center bg-brand-accent hover:bg-brand-accent-hover text-white font-medium py-3 rounded transition-colors"
              >
                {isEn ? 'Get a Quote' : 'Få et tilbud'}
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer 
        className="bg-white border-t border-gray-200 pt-16 pb-8 px-4 sm:px-6 lg:px-8 overflow-hidden"
      >
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-4">
            <div className="flex flex-col">
              <span className="font-display font-black text-black text-2xl uppercase tracking-tight leading-none">
                FREDERICIA
              </span>
              <span className="text-xs text-brand-accent font-mono font-black uppercase tracking-widest leading-none mt-1">
                ISOLERINGSPUDER
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-display font-semibold text-black text-sm uppercase tracking-wider mb-4">
              {isEn ? 'Navigation' : 'Hurtige links'}
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to={isEn ? '/en' : '/'} className="text-gray-600 hover:text-black transition-colors">
                  {isEn ? 'Home' : 'Forside'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/cases' : '/cases'} className="text-gray-600 hover:text-black transition-colors">
                  {isEn ? 'Case Studies' : 'Cases'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/news' : '/nyheder'} className="text-gray-600 hover:text-black transition-colors">
                  {isEn ? 'News' : 'Nyheder'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/about' : '/om'} className="text-gray-600 hover:text-black transition-colors">
                  {isEn ? 'About Us' : 'Om os'}
                </Link>
              </li>
              <li>
                <a 
                  href="/_assets/fredericia-isoleringspuder-aps-almindelige-salgs-og-leveringsbetingelser.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-gray-600 hover:text-black transition-colors"
                >
                  {isEn ? 'Terms of Sale & Delivery' : 'Salgs- og leveringsbetingelser'}
                </a>
              </li>
            </ul>
          </div>

          {/* Product Areas */}
          <div>
            <h3 className="font-display font-semibold text-black text-sm uppercase tracking-wider mb-4">
              {isEn ? 'Product Areas' : 'Forretningsområder'}
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to={isEn ? '/en/marine' : '/marine'} className="text-gray-600 hover:text-black transition-colors">
                  {isEn ? 'Marine Insulation' : 'Marineisolering'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/district-heating' : '/fjernvarme'} className="text-gray-600 hover:text-black transition-colors">
                  {isEn ? 'District Heating' : 'Fjernvarme'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/power-plants' : '/kraftvaerker'} className="text-gray-600 hover:text-black transition-colors">
                  {isEn ? 'CHP Power Plants' : 'Kraftvarmeværker'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/turbine-insulation' : '/turbine-isolering'} className="text-gray-600 hover:text-black transition-colors">
                  {isEn ? 'Turbine Insulation' : 'Turbine-isolering'}
                </Link>
              </li>
              <li>
                <Link to={isEn ? '/en/industry' : '/industri'} className="text-gray-600 hover:text-black transition-colors">
                  {isEn ? 'Industrial Insulation' : 'Industriisolering'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-display font-semibold text-black text-sm uppercase tracking-wider mb-4">
              {isEn ? 'Contact' : 'Kontakt'}
            </h3>
            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 text-brand-accent shrink-0 mt-0.5" />
                <span>
                  Fredericia Isoleringspuder ApS<br />
                  Gørtlervej 8, Erritsø<br />
                  7000 Fredericia
                </span>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="h-4 w-4 text-brand-accent shrink-0" />
                <a href="tel:+4552408402" className="hover:text-black transition-colors font-mono">
                  +45 52 40 84 02
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 text-brand-accent shrink-0" />
                <a href="mailto:fr@isoleringspuder.dk" className="hover:text-black transition-colors font-mono">
                  fr@isoleringspuder.dk
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-gray-500">
          <p>
            &copy; {new Date().getFullYear()} Fredericia Isoleringspuder ApS. {isEn ? 'All rights reserved.' : 'Alle rettigheder forbeholdes.'}
          </p>
          <div className="flex gap-6">
            <a 
              href="https://fredericiaisoleringspuder.dk" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-black transition-colors"
            >
              fredericiaisoleringspuder.dk
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
